import type { CommandEntryName } from "./types";

type CommandEntryCopy = {
  signInAction: string | null;
  profileAction: string | null;
  cliUpdateAction: string;
  serviceAction: string | null;
};

const DEFAULT_COPY: CommandEntryCopy = {
  signInAction: "continuing with this command",
  profileAction: "continuing with this command",
  cliUpdateAction: "using OpenMeld CLI",
  serviceAction: null,
};

const COMMAND_ENTRY_COPY_BY_NAME: Readonly<
  Record<CommandEntryName, CommandEntryCopy>
> = Object.freeze({
  root: {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "opening OpenMeld",
    serviceAction: null,
  },
  view: { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  start: {
    signInAction: "continuing setup",
    profileAction: "continuing setup",
    cliUpdateAction: "starting OpenMeld",
    serviceAction: "starting OpenMeld",
  },
  setup: {
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "setting up OpenMeld on this computer",
    serviceAction: null,
  },
  feedback: {
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "sending feedback to OpenMeld",
    serviceAction: null,
  },
  "repair.local-state": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "repairing OpenMeld",
  },
  uninstall: {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "uninstalling OpenMeld",
  },
  reset: { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  upgrade: {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "upgrading OpenMeld CLI",
  },
  "auth.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "auth.status": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "auth.login": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "auth.logout": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "organization.list": {
    signInAction: "listing organizations",
    profileAction: null,
    cliUpdateAction: "listing organizations",
    serviceAction: null,
  },
  "organization.current": {
    signInAction: "showing the active organization",
    profileAction: null,
    cliUpdateAction: "showing the active organization",
    serviceAction: null,
  },
  "organization.switch": {
    signInAction: "switching organizations",
    profileAction: null,
    cliUpdateAction: "switching organizations",
    serviceAction: null,
  },
  "organization.directory": {
    signInAction: "reading the Organization Directory",
    profileAction: null,
    cliUpdateAction: "reading the Organization Directory",
    serviceAction: null,
  },
  "organization.computers": {
    signInAction: "reading Organization Computers",
    profileAction: null,
    cliUpdateAction: "reading Organization Computers",
    serviceAction: null,
  },
  "organization.invite_link.get": {
    signInAction: "reading the Organization invite link",
    profileAction: null,
    cliUpdateAction: "reading the Organization invite link",
    serviceAction: null,
  },
  "organization.invite_link.create": {
    signInAction: "creating the Organization invite link",
    profileAction: null,
    cliUpdateAction: "creating the Organization invite link",
    serviceAction: null,
  },
  "organization.invite_link.revoke": {
    signInAction: "revoking the Organization invite link",
    profileAction: null,
    cliUpdateAction: "revoking the Organization invite link",
    serviceAction: null,
  },
  "center_agent.menu": {
    signInAction: "managing the Center Agent",
    profileAction: null,
    cliUpdateAction: "managing the Center Agent",
    serviceAction: null,
  },
  "center_agent.ask": {
    signInAction: "asking the Center Agent",
    profileAction: null,
    cliUpdateAction: "asking the Center Agent",
    serviceAction: null,
  },
  "center_agent.context": {
    signInAction: "reading Center Agent context",
    profileAction: null,
    cliUpdateAction: "reading Center Agent context",
    serviceAction: null,
  },
  "center_agent.status": {
    signInAction: "checking the Center Agent",
    profileAction: null,
    cliUpdateAction: "checking the Center Agent",
    serviceAction: null,
  },
  "center_agent.designate": {
    signInAction: "designating the Center Agent",
    profileAction: null,
    cliUpdateAction: "designating the Center Agent",
    serviceAction: null,
  },
  "center_agent.pause": {
    signInAction: "pausing the Center Agent",
    profileAction: null,
    cliUpdateAction: "pausing the Center Agent",
    serviceAction: null,
  },
  "center_agent.resume": {
    signInAction: "resuming the Center Agent",
    profileAction: null,
    cliUpdateAction: "resuming the Center Agent",
    serviceAction: null,
  },
  "center_agent.audit": {
    signInAction: "listing Center Agent audit entries",
    profileAction: null,
    cliUpdateAction: "listing Center Agent audit entries",
    serviceAction: null,
  },
  "routines.menu": {
    signInAction: "managing Routines",
    profileAction: null,
    cliUpdateAction: "managing Routines",
    serviceAction: null,
  },
  "routines.list": {
    signInAction: "listing Routines",
    profileAction: null,
    cliUpdateAction: "listing Routines",
    serviceAction: null,
  },
  "routines.create": {
    signInAction: "creating a Routine",
    profileAction: null,
    cliUpdateAction: "creating a Routine",
    serviceAction: null,
  },
  "routines.show": {
    signInAction: "showing a Routine",
    profileAction: null,
    cliUpdateAction: "showing a Routine",
    serviceAction: null,
  },
  "routines.update": {
    signInAction: "updating a Routine",
    profileAction: null,
    cliUpdateAction: "updating a Routine",
    serviceAction: null,
  },
  "routines.pause": {
    signInAction: "pausing a Routine",
    profileAction: null,
    cliUpdateAction: "pausing a Routine",
    serviceAction: null,
  },
  "routines.resume": {
    signInAction: "resuming a Routine",
    profileAction: null,
    cliUpdateAction: "resuming a Routine",
    serviceAction: null,
  },
  "routines.delete": {
    signInAction: "deleting a Routine",
    profileAction: null,
    cliUpdateAction: "deleting a Routine",
    serviceAction: null,
  },
  "routines.runs": {
    signInAction: "listing Routine Runs",
    profileAction: null,
    cliUpdateAction: "listing Routine Runs",
    serviceAction: null,
  },
  "projects.menu": {
    signInAction: "managing Agent Activity Projects",
    profileAction: null,
    cliUpdateAction: "managing Agent Activity Projects",
    serviceAction: null,
  },
  "projects.list": {
    signInAction: "listing Agent Activity Projects",
    profileAction: null,
    cliUpdateAction: "listing Agent Activity Projects",
    serviceAction: null,
  },
  "projects.create": {
    signInAction: "creating an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "creating an Agent Activity Project",
    serviceAction: null,
  },
  "projects.update": {
    signInAction: "updating an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "updating an Agent Activity Project",
    serviceAction: null,
  },
  "projects.archive": {
    signInAction: "archiving an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "archiving an Agent Activity Project",
    serviceAction: null,
  },
  "projects.bind": {
    signInAction: "binding an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "binding an Agent Activity Project",
    serviceAction: null,
  },
  "projects.connections.menu": {
    signInAction: "managing local Project connections",
    profileAction: null,
    cliUpdateAction: "managing local Project connections",
    serviceAction: null,
  },
  "projects.connections.list": {
    signInAction: "listing local Project connections",
    profileAction: null,
    cliUpdateAction: "listing local Project connections",
    serviceAction: null,
  },
  "projects.connections.remove": {
    signInAction: "removing a local Project connection",
    profileAction: null,
    cliUpdateAction: "removing a local Project connection",
    serviceAction: null,
  },
  "projects.connections.set-default": {
    signInAction: "changing the default Project connection",
    profileAction: null,
    cliUpdateAction: "changing the default Project connection",
    serviceAction: null,
  },
  "projects.unbind": {
    signInAction: "unbinding an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "unbinding an Agent Activity Project",
    serviceAction: null,
  },
  "skills.menu": {
    ...DEFAULT_COPY,
    signInAction: "reading Organization Skills",
    profileAction: null,
  },
  "skills.ensure": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "skills.install": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "skills.uninstall": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "skills.list": {
    ...DEFAULT_COPY,
    signInAction: "listing Organization Skills",
    profileAction: null,
  },
  "plugins.list": {
    ...DEFAULT_COPY,
    signInAction: "listing available plugin accounts",
    profileAction: null,
  },
  "plugins.actions": {
    ...DEFAULT_COPY,
    signInAction: "discovering plugin actions",
    profileAction: null,
  },
  "plugins.call": {
    ...DEFAULT_COPY,
    signInAction: "running a plugin action",
    profileAction: null,
  },
  "skills.show": {
    ...DEFAULT_COPY,
    signInAction: "reading an Organization Skill",
    profileAction: null,
  },
  "skills.load": {
    ...DEFAULT_COPY,
    signInAction: "loading an Organization Skill",
    profileAction: null,
  },
  "skills.check": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "skills.update": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.detect": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.list": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.manage": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.enable": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.repair": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.disable": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.show": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.custom.list": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.custom.add": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.custom.update": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.custom.remove": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.config": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "computer.list": {
    signInAction: "listing computers",
    profileAction: null,
    cliUpdateAction: "listing computers",
    serviceAction: null,
  },
  "computer.disconnect": {
    signInAction: "disconnecting this computer",
    profileAction: null,
    cliUpdateAction: "disconnecting this computer",
    serviceAction: null,
  },
  "computer.current": {
    signInAction: "showing this computer",
    profileAction: null,
    cliUpdateAction: "showing this computer",
    serviceAction: null,
  },
  "computer.rename": {
    signInAction: "renaming this computer",
    profileAction: null,
    cliUpdateAction: "renaming this computer",
    serviceAction: "renaming this computer",
  },
  "service.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "service.run": {
    signInAction: "starting OpenMeld Service",
    profileAction: null,
    cliUpdateAction: "starting OpenMeld Service",
    serviceAction: "starting OpenMeld Service",
  },
  "service.status": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.interrupt": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.cancel": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.snapshot": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.trace": {
    signInAction: "reading a dispatch trace",
    profileAction: "reading a dispatch trace",
    cliUpdateAction: "reading a dispatch trace",
    serviceAction: null,
  },
  "service.install": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.autostart": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "changing OpenMeld Service automatic startup",
  },
  "service.uninstall": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.reinstall": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.stop": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "space.menu": {
    signInAction: "managing spaces",
    profileAction: "managing spaces",
    cliUpdateAction: "opening Spaces",
    serviceAction: null,
  },
  "space.create": {
    signInAction: "creating a space",
    profileAction: "creating a space",
    cliUpdateAction: "creating a space",
    serviceAction: null,
  },
  "space.delete": {
    signInAction: "deleting a space",
    profileAction: "deleting a space",
    cliUpdateAction: "deleting a space",
    serviceAction: null,
  },
  "space.join": {
    signInAction: "joining a space",
    profileAction: "joining a space",
    cliUpdateAction: "joining a space",
    serviceAction: null,
  },
  "space.add-me": {
    signInAction: "adding yourself to a space",
    profileAction: "adding yourself to a space",
    cliUpdateAction: "adding yourself to a space",
    serviceAction: null,
  },
  "space.add-agents": {
    signInAction: "adding agent profiles to a space",
    profileAction: "adding agent profiles to a space",
    cliUpdateAction: "adding agent profiles to a space",
    serviceAction: null,
  },
  "space.add-human-profiles": {
    signInAction: "adding human profiles to a space",
    profileAction: "adding human profiles to a space",
    cliUpdateAction: "adding human profiles to a space",
    serviceAction: null,
  },
  "space.add-members": {
    signInAction: "adding members to a space",
    profileAction: "adding members to a space",
    cliUpdateAction: "adding members to a space",
    serviceAction: null,
  },
  "space.remove-members": {
    signInAction: "removing members from a space",
    profileAction: "removing members from a space",
    cliUpdateAction: "removing members from a space",
    serviceAction: null,
  },
  "space.leave": {
    signInAction: "leaving a space",
    profileAction: "leaving a space",
    cliUpdateAction: "leaving a space",
    serviceAction: null,
  },
  "space.files.handoff": {
    signInAction: "handing off Space files",
    profileAction: "handing off Space files",
    cliUpdateAction: "handing off Space files",
    serviceAction: null,
  },
  "space.files.working-copy": {
    signInAction: "working on Space files locally",
    profileAction: "working on Space files locally",
    cliUpdateAction: "working on Space files locally",
    serviceAction: null,
  },
  "space.files.list": {
    signInAction: "listing Space files",
    profileAction: "listing Space files",
    cliUpdateAction: "listing Space files",
    serviceAction: null,
  },
  "space.files.read": {
    signInAction: "reading a Space file",
    profileAction: "reading a Space file",
    cliUpdateAction: "reading a Space file",
    serviceAction: null,
  },
  "space.files.upload": {
    signInAction: "uploading a Space file",
    profileAction: "uploading a Space file",
    cliUpdateAction: "uploading a Space file",
    serviceAction: null,
  },
  "space.files.write": {
    signInAction: "updating a Space file",
    profileAction: "updating a Space file",
    cliUpdateAction: "updating a Space file",
    serviceAction: null,
  },
  "space.files.download": {
    signInAction: "downloading a Space file",
    profileAction: "downloading a Space file",
    cliUpdateAction: "downloading a Space file",
    serviceAction: null,
  },
  "space.files.mkdir": {
    signInAction: "creating a Space folder",
    profileAction: "creating a Space folder",
    cliUpdateAction: "creating a Space folder",
    serviceAction: null,
  },
  "space.files.rename": {
    signInAction: "renaming a Space file or folder",
    profileAction: "renaming a Space file or folder",
    cliUpdateAction: "renaming a Space file or folder",
    serviceAction: null,
  },
  "space.files.move": {
    signInAction: "moving a Space file or folder",
    profileAction: "moving a Space file or folder",
    cliUpdateAction: "moving a Space file or folder",
    serviceAction: null,
  },
  "space.files.trash": {
    signInAction: "moving a Space file or folder to trash",
    profileAction: "moving a Space file or folder to trash",
    cliUpdateAction: "moving a Space file or folder to trash",
    serviceAction: null,
  },
  "space.files.history": {
    signInAction: "viewing a Space file's history",
    profileAction: "viewing a Space file's history",
    cliUpdateAction: "viewing a Space file's history",
    serviceAction: null,
  },
  "space.files.restore": {
    signInAction: "restoring a Space file or folder",
    profileAction: "restoring a Space file or folder",
    cliUpdateAction: "restoring a Space file or folder",
    serviceAction: null,
  },
  "space.files.restore-version": {
    signInAction: "restoring a Space file version",
    profileAction: "restoring a Space file version",
    cliUpdateAction: "restoring a Space file version",
    serviceAction: null,
  },
  "space.watch": {
    signInAction: "watching a space",
    profileAction: "watching a space",
    cliUpdateAction: "watching a space",
    serviceAction: null,
  },
  "space.send": {
    signInAction: "sending a message",
    profileAction: "sending a message",
    cliUpdateAction: "sending a message",
    serviceAction: null,
  },
  "space.history": {
    signInAction: "reading space history",
    profileAction: "reading space history",
    cliUpdateAction: "reading space history",
    serviceAction: null,
  },
  "space.thread": {
    signInAction: "reading a space thread",
    profileAction: "reading a space thread",
    cliUpdateAction: "reading a space thread",
    serviceAction: null,
  },
  "space.board": {
    signInAction: "reading a space board",
    profileAction: "reading a space board",
    cliUpdateAction: "reading a space board",
    serviceAction: null,
  },
  "space.members": {
    signInAction: "reading the Space member directory",
    profileAction: "reading the Space member directory",
    cliUpdateAction: "reading the Space member directory",
    serviceAction: null,
  },
  "space.contract": {
    signInAction: "reading a space contract",
    profileAction: "reading a space contract",
    cliUpdateAction: "reading a space contract",
    serviceAction: null,
  },
  "space.contract.set": {
    signInAction: "updating a space contract",
    profileAction: "updating a space contract",
    cliUpdateAction: "updating a space contract",
    serviceAction: null,
  },
  "space.publication.set": {
    signInAction: "updating Space publication mode",
    profileAction: "updating Space publication mode",
    cliUpdateAction: "updating Space publication mode",
    serviceAction: null,
  },
  "space.access": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "checking Space access",
  },
  "space.status": {
    signInAction: "reading space status",
    profileAction: "reading space status",
    cliUpdateAction: "reading space status",
    serviceAction: null,
  },
  "space.updates": {
    signInAction: "reading space updates",
    profileAction: "reading space updates",
    cliUpdateAction: "reading space updates",
    serviceAction: null,
  },
  "space.result": {
    signInAction: "reading a space dispatch result",
    profileAction: "reading a space dispatch result",
    cliUpdateAction: "reading a space dispatch result",
    serviceAction: null,
  },
  "space.list": {
    signInAction: "listing spaces",
    profileAction: "listing spaces",
    cliUpdateAction: "listing spaces",
    serviceAction: null,
  },
  "space.list.organization": {
    signInAction: "listing Organization Spaces",
    profileAction: "listing Organization Spaces",
    cliUpdateAction: "listing Organization Spaces",
    serviceAction: null,
  },
  "space.join.self_serve": {
    signInAction: "joining a public Organization Space",
    profileAction: "joining a public Organization Space",
    cliUpdateAction: "joining a public Organization Space",
    serviceAction: null,
  },
  "space.wake_progress": {
    signInAction: "reading live Wake progress",
    profileAction: "reading live Wake progress",
    cliUpdateAction: "reading live Wake progress",
    serviceAction: null,
  },
  "space.wake_stop": {
    signInAction: "stopping a Wake",
    profileAction: "stopping a Wake",
    cliUpdateAction: "stopping a Wake",
    serviceAction: null,
  },
  "space.password": {
    signInAction: "updating a space password",
    profileAction: "updating a space password",
    cliUpdateAction: "updating a space password",
    serviceAction: null,
  },
  "space.guide": {
    signInAction: "reading space guide",
    profileAction: "reading space guide",
    cliUpdateAction: "reading space guide",
    serviceAction: null,
  },
  "space.guide.set": {
    signInAction: "updating space guide",
    profileAction: "updating space guide",
    cliUpdateAction: "updating space guide",
    serviceAction: null,
  },
  "space.guide.clear": {
    signInAction: "clearing space guide",
    profileAction: "clearing space guide",
    cliUpdateAction: "clearing space guide",
    serviceAction: null,
  },
  doctor: {
    signInAction: "running doctor checks",
    profileAction: "running doctor checks",
    cliUpdateAction: "running doctor checks",
    serviceAction: null,
  },
  whoami: {
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "checking current OpenMeld status",
    serviceAction: null,
  },
  "profile.menu": {
    signInAction: "managing profiles",
    profileAction: null,
    cliUpdateAction: "managing profiles",
    serviceAction: null,
  },
  "profile.create": {
    signInAction: "creating a profile",
    profileAction: null,
    cliUpdateAction: "creating a profile",
    serviceAction: null,
  },
  "profile.current-session": {
    signInAction: "finding this Agent session's profile",
    profileAction: null,
    cliUpdateAction: "finding this Agent session's profile",
    serviceAction: null,
  },
  "profile.list": {
    signInAction: "listing profiles",
    profileAction: null,
    cliUpdateAction: "listing profiles",
    serviceAction: null,
  },
  "profile.set": {
    signInAction: "choosing a profile",
    profileAction: null,
    cliUpdateAction: "choosing a profile",
    serviceAction: null,
  },
  "profile.show": {
    signInAction: "showing a profile",
    profileAction: null,
    cliUpdateAction: "showing a profile",
    serviceAction: null,
  },
  "profile.update": {
    signInAction: "updating a profile",
    profileAction: null,
    cliUpdateAction: "updating a profile",
    serviceAction: null,
  },
  "profile.execution-mode": {
    signInAction: "managing independent-task admission",
    profileAction: null,
    cliUpdateAction: "managing independent-task admission",
    serviceAction: null,
  },
  "profile.delete": {
    signInAction: "deleting a profile",
    profileAction: null,
    cliUpdateAction: "deleting a profile",
    serviceAction: null,
  },
  "profile.requests.create": {
    signInAction: "requesting an Agent Profile",
    profileAction: null,
    cliUpdateAction: "requesting an Agent Profile",
    serviceAction: null,
  },
  "profile.requests.list": {
    signInAction: "listing Agent Profile requests",
    profileAction: null,
    cliUpdateAction: "listing Agent Profile requests",
    serviceAction: null,
  },
  "profile.requests.approve": {
    signInAction: "approving an Agent Profile request",
    profileAction: null,
    cliUpdateAction: "approving an Agent Profile request",
    serviceAction: null,
  },
  "profile.requests.decline": {
    signInAction: "declining an Agent Profile request",
    profileAction: null,
    cliUpdateAction: "declining an Agent Profile request",
    serviceAction: null,
  },
  "profile.requests.cancel": {
    signInAction: "canceling an Agent Profile request",
    profileAction: null,
    cliUpdateAction: "canceling an Agent Profile request",
    serviceAction: null,
  },
  "profile.requests.retry": {
    signInAction: "retrying an Agent Profile request",
    profileAction: null,
    cliUpdateAction: "retrying an Agent Profile request",
    serviceAction: null,
  },
  "profile.requests.preferences.get": {
    signInAction: "showing Agent Profile request preferences",
    profileAction: null,
    cliUpdateAction: "showing Agent Profile request preferences",
    serviceAction: null,
  },
  "profile.requests.preferences.set": {
    signInAction: "updating Agent Profile request preferences",
    profileAction: null,
    cliUpdateAction: "updating Agent Profile request preferences",
    serviceAction: null,
  },
});

export function getCommandEntryCopy(
  entryName: CommandEntryName
): CommandEntryCopy {
  return COMMAND_ENTRY_COPY_BY_NAME[entryName];
}
